import type { StepState } from '@epilot/journey-logic-commons'
import { useEffect } from 'react'

import { useJourneyContext } from '../blocks-renderers'
import { selectSelectedJourneyId } from '../store/selectors/config'
import {
  selectBlocksMapCache,
  selectShownBlocks
} from '../store/selectors/runtime'

import { useAppStoreSelector } from './useAppStoreSelector'

/**
 * Removes state for hidden blocks from the steps state array
 */
const removeHiddenBlocksFromStepsState = (
  stepsStateArray: StepState[],
  blocksMapCache: Map<number, Map<string, any>> | undefined,
  shownBlocks: Set<string>
): StepState[] => {
  return stepsStateArray.reduce<StepState[]>((acc, stepState, stepIndex) => {
    for (const blockName of Object.keys(stepState)) {
      const block = blocksMapCache?.get(stepIndex)?.get(blockName)

      if (block && !shownBlocks.has(block.id)) {
        delete acc[stepIndex][blockName] // Remove block state if it's not shown
      }
    }

    return acc
  }, structuredClone(stepsStateArray))
}

/**
 * Hook that synchronizes hidden blocks state by removing form data
 * for blocks that are no longer visible due to logic conditions.
 */
export const useSyncHiddenBlocksState = () => {
  const {
    context: { _stepsStateArray },
    updateContext
  } = useJourneyContext()

  const blocksMapCache = useAppStoreSelector(selectBlocksMapCache)

  const journeyId = useAppStoreSelector(selectSelectedJourneyId)

  const shownBlocks = useAppStoreSelector(selectShownBlocks)

  // Remove state for hidden blocks
  useEffect(() => {
    if (!journeyId) return

    const updatedStepsState = removeHiddenBlocksFromStepsState(
      _stepsStateArray,
      blocksMapCache,
      shownBlocks
    )

    updateContext((context) => {
      return {
        ...context,
        journeyStepStateMap: {
          ...context.journeyStepStateMap,
          [journeyId]: updatedStepsState
        }
      }
    })
  }, [shownBlocks, updateContext])
}
